import React, { useContext, useState, useEffect } from 'react';
import getConfig from 'next/config';
import get from 'lodash.get';
import i18next from 'i18next';
import fetch from 'isomorphic-fetch';
import classNames from 'classnames';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import { PersonContext } from 'lib/ContextTypes/person';
import { timeFrom } from 'lib/DateTime';

import Picture from 'components/Picture';
import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Ad from 'components/Ad';

const PersonFeed = () => {
  const { publicRuntimeConfig: { API_URL_BENTO_PUBLIC } } = getConfig();

  const person = useContext(PersonContext);
  const [personContent, setPersonContent] = useState(person.content);
  const [pageCount, setPageCount] = useState(1);

  const getUnibrow = (vertical, unibrowText) => {
    if (vertical && unibrowText) return (`${vertical} / ${unibrowText}`);
    if (vertical && !unibrowText) return vertical;
    if (!vertical && unibrowText) return unibrowText;
    return null;
  };

  const getFeedItemInfoWrapperColGridClass = (date, imageURL) => {
    if (!date && !imageURL) return 'grid-col-10-m';
    if (!date && imageURL) return 'grid-col-8-m';
    if (date && !imageURL) return 'grid-col-9-m';

    return 'flex-grow-1-m grid-col-6-m';
  };

  const isLastArticlePage = () => {
    const { totalPages } = personContent.pagination;
    return pageCount === totalPages;
  };

  const getArticlesFromApi = async () => {
    const payload = await fetch(`${API_URL_BENTO_PUBLIC}/person/content?id=${person.id}&page=${pageCount}`)
      .then((response) => response.json())
      .catch(() => {
        throw new Error('Fetching Person failed');
      });
    const updatedPersonContent = { ...personContent };
    const updatedContentItems = personContent.items.concat(get(payload, ['data', 'person', 'content', 'items'], []));

    updatedPersonContent.items = updatedContentItems;
    setPersonContent(updatedPersonContent);
  };

  useEffect(() => {
    if (pageCount > 1) {
      getArticlesFromApi();
    }
  }, [pageCount]);

  const renderArticleImage = (imageURL, additionalClassNames) => {
    if (imageURL) {
      return (
        <div className={`person-feed-item__image-wrapper ${additionalClassNames}`}>
          <Picture
            responsiveFlavors={{
              s: AIMS_FLAVORS.FOCAL_200x100,
              m: AIMS_FLAVORS.FOCAL_220x110,
              l: AIMS_FLAVORS.FOCAL_280x140,
              xl: AIMS_FLAVORS.FOCAL_260x130,
            }}
            url={imageURL}
          />
        </div>
      );
    }
    return null;
  };

  const renderArticles = personContent.items.map((article) => {
    const date = get(article, 'datePublished');
    const unibrowURL = get(article, ['unibrow', 'url', 'primary'], '');
    const vertical = get(article, 'vertical');
    const unibrowText = get(article, ['unibrow', 'text']);
    const concatenatedUnibrowText = getUnibrow(vertical, unibrowText);
    const articleUrl = get(article, ['url', 'primary']);
    const articleHeadline = get(article, ['headline', 'primary']);
    const articleDescription = get(article, ['description', 'primary'], '');
    const imageURL = get(article, ['primaryImage', 'url', 'primary']);

    return (
      <div className={classNames('person-feed-item__wrapper df flex-column flex-row-m flex-nowrap-m', { 'person-feed-item__wrapper--last-page': isLastArticlePage() })} key={article.id}>

        <div className={classNames('person-feed-item__unibrow-wrapper', { dn: !date })}>
          {concatenatedUnibrowText && (
            <a href={unibrowURL} className="dib dn-m">
              <h2 className="person-feed-item__unibrow">
                {concatenatedUnibrowText}
              </h2>
            </a>
          )}
          {date && (
            <div className="person-feed-item__timestamp dib db-m">
              {date && `${i18next.t('time_ago', { time: timeFrom(date) })}`}
            </div>
          )}
        </div>

        <div className={classNames('person-feed-item__info-wrapper', getFeedItemInfoWrapperColGridClass(date, imageURL))}>
          {concatenatedUnibrowText && (
            <a href={unibrowURL} className="dn db-m">
              <h2 className="person-feed-item__unibrow">
                {concatenatedUnibrowText}
              </h2>
            </a>
          )}
          <a href={articleUrl}>
            {articleHeadline && (
              <h2 className="person-feed-item__headline">{articleHeadline}</h2>
            )}
            <div className={classNames({ df: imageURL })}>
              {articleDescription && (
                <div className="person-feed-item__description">{articleDescription}</div>
              )}
              {renderArticleImage(imageURL, 'db dn-m')}
            </div>
          </a>
        </div>

        {renderArticleImage(imageURL, 'dn db-m')}
      </div>
    );
  });

  return (
    <div className="person-feed layout-grid-container">
      <div className="layout-grid-item grid-col-12 grid-col-8-xl unset-horizontal-border-width-l">
        <div className="layout-grid-item">
          <div className="person-feed__header--top-border" />
        </div>
        <h1 className="person-feed__header layout-grid-item">{`Latest from ${person.name}`}</h1>
        {personContent.items && (
          <div className={classNames('person-feed-items__container layout-grid-item', { 'person-feed-items__container--last-page': isLastArticlePage() })}>{renderArticles}</div>
        )}
        {!isLastArticlePage() && (
          <ButtonHoverAnimation
            type="button"
            additionalClasses="person-feed__load-more-button"
            onClick={() => { setPageCount(pageCount + 1); }}
            title={i18next.t('Load More')}
            size="normal"
          />
        )}
      </div>
      <div className="person-feed__ads-container layout-grid-item grid-col-12 grid-col-4-xl">
        <Ad
          slot="boxinline"
          adClass="person-feed__mobile--ad"
          offsetViewport={50}
        />
        <Ad
          slot="boxrail"
          adClass="person-feed__right-rail--ad"
          sticky
        />
      </div>
    </div>
  );
};

PersonFeed.propTypes = {};

export { PersonFeed };
